import { Component, OnInit, ViewChild } from '@angular/core';
import { ProspectActivitySummary } from '../domain/prospect.model';
import { Table } from 'primeng/table';
import { UIChart } from 'primeng/chart';
import { ProspectsService } from '../shared/services/prospects.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-prospect-activity-summary',
  templateUrl: './prospect-activity-summary.component.html',
  styleUrls: ['./prospect-activity-summary.component.scss']
})
export class ProspectActivitySummaryComponent implements OnInit {
  @ViewChild('dtProspectActivitySummary') activityTable: Table;
  @ViewChild('prospectActivityChart') prospectActivityChart: UIChart;

  prospectActivitySummary: ProspectActivitySummary[] = [];
  filteredProspectActivitySummary: ProspectActivitySummary[] = [];
  prospectActivityChartData: any;
  prospectActivityChartOptions: any;
  selectedActivityGroupBy: string = 'technician';
  selectedCountType: string = 'prospectLeaderCount';
  technicianOptions: any[] = [];

  activityGroupByOptions: any[] = [
    { label: 'Technical Lead', value: 'technician' },
    { label: 'Customer Name', value: 'customerName' }
  ];

  countTypeOptions: any[] = [
    { label: 'Prospect Count', value: 'prospectLeaderCount' },
    { label: 'Activity Count', value: 'activityAttendedCount' }
  ];

  constructor(private prospectsService: ProspectsService) { }

  ngOnInit(): void {
    this.loadProspectActivitySummary();
    this.initializeProspectActivityChart();
  }

  loadProspectActivitySummary() {
    this.prospectsService.getProspectActivitySummary()
      .toPromise()
      .then(response => {
        this.prospectActivitySummary = response;
        this.filteredProspectActivitySummary = response;
        
        // Extract unique technicians for filter dropdown
        const uniqueTechnicians = [...new Set(response.map(item => item.technician))];
        this.technicianOptions = uniqueTechnicians.map(tech => ({
          label: tech,
          value: tech
        }));
        
        this.updateProspectActivityChartData();
      })
      .catch(error => {
        console.error('Error loading prospect activity summary:', error);
      });
  }

  initializeProspectActivityChart() {
    this.prospectActivityChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          beginAtZero: true
        }
      },
      plugins: {
        tooltip: {
          callbacks: {
            title: (tooltipItems) => {
              return tooltipItems[0].label;
            },
            label: (tooltipItem) => {
              const datasetLabel = tooltipItem.dataset.label;
              const value = tooltipItem.raw;
              const countType = this.selectedCountType === 'prospectLeaderCount' ? 'prospects' : 'activities';
              return `${datasetLabel}: ${value} ${countType}`;
            },
            footer: (tooltipItems) => {
              const dataIndex = tooltipItems[0].dataIndex;
              const allDatasets = tooltipItems[0].chart.data.datasets;
              
              let columnTotal = 0;
              for (let i = 0; i < allDatasets.length; i++) {
                if (allDatasets[i].data[dataIndex]) {
                  columnTotal += allDatasets[i].data[dataIndex];
                }
              }
              
              const countType = this.selectedCountType === 'prospectLeaderCount' ? 'prospects' : 'activities';
              return `Total: ${columnTotal} ${countType}`;
            }
          },
          usePointStyle: true,
          boxWidth: 6
        },
        legend: {
          position: 'right',
        }
      }
    };
  }

  updateProspectActivityChartData() {
    let groupedData;
    if (this.selectedActivityGroupBy === 'technician') {
      groupedData = this.groupActivityByTechnician();
    } else {
      groupedData = this.groupActivityByCustomer();
    }

    const labels = Object.keys(groupedData);
    const datasets = this.createActivityDatasets(groupedData);

    this.prospectActivityChartData = {
      labels: labels,
      datasets: datasets
    };

    if (this.prospectActivityChart) {
      this.prospectActivityChart.reinit();
    }
  }

  groupActivityByTechnician() {
    return this.filteredProspectActivitySummary.reduce((acc, item) => {
      if (!acc[item.technician]) {
        acc[item.technician] = {};
      }
      
      // Aggregate by customer name for each technician
      if (!acc[item.technician][item.customerName]) {
        acc[item.technician][item.customerName] = 0;
      }
      acc[item.technician][item.customerName] += item[this.selectedCountType];
      
      return acc;
    }, {});
  }

  groupActivityByCustomer() {
    return this.filteredProspectActivitySummary.reduce((acc, item) => {
      if (!acc[item.customerName]) {
        acc[item.customerName] = {};
      }
      
      // Aggregate by technician for each customer
      if (!acc[item.customerName][item.technician]) {
        acc[item.customerName][item.technician] = 0;
      }
      acc[item.customerName][item.technician] += item[this.selectedCountType];
      
      return acc;
    }, {});
  }

  createActivityDatasets(groupedData: { [key: string]: { [key: string]: number } }) {
    const groups = new Set<string>();
    Object.values(groupedData).forEach((data) => {
      Object.keys(data).forEach((group) => groups.add(group));
    });

    const groupArray = Array.from(groups);
    const colors = [
      '#42A5F5', '#66BB6A', '#FFA726', '#26C6DA', '#7E57C2',
      '#EC407A', '#AB47BC', '#5C6BC0', '#29B6F6', '#26A69A'
    ];

    return groupArray.map((group, index) => {
      const data = Object.keys(groupedData).map((key) => groupedData[key][group] || 0);

      return {
        label: group,
        data: data,
        backgroundColor: colors[index % colors.length]
      };
    });
  }

  onActivityGroupByChange() {
    this.updateProspectActivityChartData();
  }

  onCountTypeChange() {
    this.updateProspectActivityChartData();
  }

  onActivityTableFilter(event: any) {
    this.filteredProspectActivitySummary = event.filteredValue || [];
    this.updateProspectActivityChartData();
  }

  exportProspectActivityPdf() {
    const doc = new jsPDF('landscape', 'pt', 'a4');

    const columns = [
      { header: '#', dataKey: 'index' },
      { header: 'Technical Lead', dataKey: 'technician' },
      { header: 'Customer Name', dataKey: 'customerName' },
      { header: 'Prospect Count', dataKey: 'prospectLeaderCount' },
      { header: 'Activity Count', dataKey: 'activityAttendedCount' }
    ];

    const data = this.filteredProspectActivitySummary;

    const rows = data.map((row: any, index: number) => ({
      index: index + 1,
      technician: row.technician,
      customerName: row.customerName,
      prospectLeaderCount: row.prospectLeaderCount,
      activityAttendedCount: row.activityAttendedCount
    }));

    autoTable(doc, {
      columns: columns,
      body: rows,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 10,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        index: { cellWidth: 26, halign: 'center' },
        technician: { cellWidth: 60, halign: 'center' },
        customerName: { cellWidth: 60, halign: 'center' },
        prospectLeaderCount: { cellWidth: 60, halign: 'center' },
        activityAttendedCount: { cellWidth: 60, halign: 'center' }
      },
      margin: { top: 20 }
    });

    doc.save('ProspectActivitySummary.pdf');
  }

  exportProspectActivityExcel() {
    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(this.filteredProspectActivitySummary.map(summary => ({
        'Technical Lead': summary.technician,
        'Customer Name': summary.customerName,
        'Prospect Count': summary.prospectLeaderCount,
        'Activity Count': summary.activityAttendedCount
      })));

      const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
      const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      this.saveAsExcelFile(excelBuffer, "ProspectActivitySummary");
    });
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
  }

  exportProspectActivityChartAsImage() {
    if (this.prospectActivityChart && this.prospectActivityChart.chart) {
      const chart = this.prospectActivityChart.chart;
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const link = document.createElement('a');
      link.href = base64Image;
      link.download = 'prospect-activity-summary-chart.png';
      link.click();
    }
  }

  exportProspectActivityChartAsPDF() {
    if (this.prospectActivityChart && this.prospectActivityChart.chart) {
      const chart = this.prospectActivityChart.chart;
      const canvas = chart.canvas;
      const ctx = canvas.getContext('2d');

      ctx.save();
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const base64Image = canvas.toDataURL('image/png');
      ctx.restore();

      const imgWidth = 280;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      const pdf = new jsPDF('landscape');
      pdf.addImage(base64Image, 'PNG', 10, 10, imgWidth, imgHeight);
      pdf.save('prospect-activity-summary-chart.pdf');
    }
  }
}
